/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 調査詳細の「依頼内容」カード。読み取り時は先頭に種別（カテゴリ）を色付きバッジで表示し、背景 / 対応依頼 / 対象ユーザー ID に加え WFID（ワークフロー ID）と CS チケット URL を表示する。編集可能（Firestore 発番）な依頼は編集ボタンで背景 / 対応依頼 の textarea、種別 / サービス のドロップダウン、WFID / CS URL / 対象ユーザー ID（カンマ区切り）の input を開き editContent で保存する。編集中は保存済みの種別バッジを出さない（ドロップダウンの選択値と食い違うため）。システムオペレーションの依頼は種別を変えられない（API が手動の変更を 400 にする）ので、編集中もバッジのまま見せてドロップダウンを出さない。種別 / サービスは変更されたときだけ送る（毎回送ると他者の訂正を巻き戻し、本文だけの編集でも分類を書き直す）。WFID / 対象ユーザー ID は現在空でも「追加」導線で後付けできる（起票後に WFID を足す・CS がユーザーを特定してから ID を足す運用に対応）。対象ユーザー ID は欄を触ったときだけ起票と同じ規則（数字のみ）で検証し（違反中は保存を止める）、触ったときだけ送る（触っていない古いデータで他フィールドの保存を止めない）。CS URL はリンク表示。翻訳（ja/en/vi）は原文を書き換えず表示のみ差し替える。legacy 依頼は読み取り専用。ops.requests.$id 本体から切り出した表示 + 編集コンポーネント
 * @graph-connects OpsDetailPage [references] 依頼詳細ページ本体から依頼内容カード（表示 + 編集 + 翻訳ヘッダ）を分離
 * @graph-connects useEditOpsRequestContent [calls] 依頼内容（背景 / 対応依頼 / WFID / CS URL / 種別 / サービス / 対象ユーザー ID）の編集（PATCH）を実行
 * @graph-connects parseUserIds [calls] 対象ユーザー ID のカンマ区切り入力を配列へ分割（起票フォームと同じ規則）
 * @graph-connects isOpsUserId [calls] 対象ユーザー ID の形式検証（起票と同じ数字のみの規則）
 * @graph-connects CategoryBadge [references] 種別（調査依頼 / 状態変更 / データ修正 / SQL レビュー）の色付きバッジ表示
 * @graph-connects opsCategoryLabel [calls] 種別ドロップダウンの選択肢ラベルを現在の言語へ変換
 */
import { useState, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { Card, CardContent, CardHeader } from '@cortex/web-design-system/components/ui/Card';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import {
  isOpsSystemCategory,
  isOpsUserId,
  OPS_SELECTABLE_CATEGORIES,
  OPS_SERVICE_LABELS,
  OPS_SERVICE_SLUG_VALUES,
  parseUserIds,
} from '@cortex/ops-core';
import { useEditOpsRequestContent } from '../../../hooks/useOps.js';
import { CategoryBadge, opsCategoryLabel } from './OpsPrimitives.js';
import type { OpsCategory, OpsRequest, OpsService } from '../../../types/ops.js';
import type { OpsTranslateTarget } from '../../../hooks/useOps.js';

/** 種別の選択肢（Fatal は自動付与のため人手では選ばせない。ops-core が単一 source）。 @graph-connects none */
const CATEGORY_OPTIONS: readonly OpsCategory[] = OPS_SELECTABLE_CATEGORIES;

/** サービスの選択肢（ops-core が単一 source。サービス追加に自動追従）。 @graph-connects none */
const SERVICE_OPTIONS: readonly OpsService[] = OPS_SERVICE_SLUG_VALUES;

/** 翻訳先言語の選択肢（値と i18n ラベルキー）。 @graph-connects none */
export interface OpsTranslateLangOption {
  /** 言語コード。 */
  value: OpsTranslateTarget;
  /** i18n ラベルキー。 */
  labelKey: string;
}

/** OpsRequestContentCard の props。 */
export interface OpsRequestContentCardProps {
  /** 表示対象の依頼（id / background / requested / userIds / wfid / csTicketUrl を参照）。 */
  request: OpsRequest;
  /** Firestore 発番（編集可能）依頼かどうか。false=legacy 読み取り専用。 */
  editable: boolean;
  /** 表示する背景（翻訳中は訳文、通常は request.background）。 */
  shownBackground: string;
  /** 表示する対応依頼（翻訳中は訳文、通常は request.requested）。 */
  shownRequested: string;
  /** 現在表示中の翻訳言語（null = 原文表示）。翻訳ボタンのアクティブ表示に使う。 */
  translationLang: OpsTranslateTarget | null;
  /** 翻訳中か。 */
  isTranslating: boolean;
  /** 翻訳に失敗したか。 */
  isTranslateError: boolean;
  /** 翻訳先言語の選択肢。 */
  translateOptions: OpsTranslateLangOption[];
  /** 指定言語へ翻訳する。 */
  onTranslate: (target: OpsTranslateTarget) => void;
  /** 原文表示へ戻す。 */
  onResetTranslation: () => void;
  /** 翻訳関数。 */
  t: TFunction;
}

/**
 * 依頼内容カードを描画する。編集可能な依頼は編集ボタンで背景 / 対応依頼 / WFID / CS URL / 対象ユーザー ID の
 * 編集フォームを開き、保存で editContent を呼ぶ。read ビューでは WFID / CS URL / 対象ユーザー ID をフィールド表示し、
 * WFID / 対象ユーザー ID が空なら「追加」導線を出す。
 * 翻訳ヘッダ（ja/en/vi + 原文に戻す）は編集中でないときだけ出す（原文を編集するため）。
 * @graph-connects none
 */
export function OpsRequestContentCard({
  request,
  editable,
  shownBackground,
  shownRequested,
  translationLang,
  isTranslating,
  isTranslateError,
  translateOptions,
  onTranslate,
  onResetTranslation,
  t,
}: OpsRequestContentCardProps): ReactNode {
  const editMutation = useEditOpsRequestContent();
  const [editing, setEditing] = useState(false);
  const [backgroundDraft, setBackgroundDraft] = useState(request.background);
  const [requestedDraft, setRequestedDraft] = useState(request.requested);
  const [wfidDraft, setWfidDraft] = useState(request.wfid ?? '');
  const [csUrlDraft, setCsUrlDraft] = useState(request.csTicketUrl ?? '');
  const [categoryDraft, setCategoryDraft] = useState<OpsCategory>(request.category);
  const [serviceDraft, setServiceDraft] = useState<OpsService>(request.service);
  const [userIdsDraft, setUserIdsDraft] = useState(request.userIds.join(', '));
  // 欄を触ったときだけ検証・送信する。触っていない古いデータ（数字以外・カンマ入りの旧単一 ID）が
  // 他フィールドの保存を止めたり、無関係な保存で書き換わったりしないようにする。
  const userIdsDirty = userIdsDraft !== request.userIds.join(', ');
  const draftUserIds = parseUserIds(userIdsDraft);
  const userIdsInvalid = userIdsDirty && !draftUserIds.every(isOpsUserId);

  // 保存済みの種別が選択肢に無い（列挙外の値が入った古いデータ）ときは現在値を先頭に足す。
  // 足さないと select が先頭を選んだように見え、実際と違う種別が表示される。
  const categoryOptions: readonly OpsCategory[] = CATEGORY_OPTIONS.includes(request.category)
    ? CATEGORY_OPTIONS
    : [request.category, ...CATEGORY_OPTIONS];
  // システムオペレーションの種別は取り込みだけが扱う（API は手動の変更を 400 にする）。選べない選択肢は出さない。
  const categoryLocked = isOpsSystemCategory(request.category);

  // 編集モードを開く。原文（翻訳前）をドラフトに読み直し、翻訳表示中でも原文を編集できるようにする。
  const openEdit = (): void => {
    if (!editable) return;
    setBackgroundDraft(request.background);
    setRequestedDraft(request.requested);
    setWfidDraft(request.wfid ?? '');
    setCsUrlDraft(request.csTicketUrl ?? '');
    setCategoryDraft(request.category);
    setServiceDraft(request.service);
    setUserIdsDraft(request.userIds.join(', '));
    editMutation.reset();
    onResetTranslation();
    setEditing(true);
  };

  const cancelEdit = (): void => {
    setEditing(false);
  };

  // WFID / CS URL は空文字なら null（消去）で送る。種別・サービス・対象ユーザー ID は変わったときだけ送る:
  // 毎回送ると他人の訂正を巻き戻し（競合検知が無い）、本文だけの編集でも BQ ミラーに 1 行積み、
  // 選択肢に無い種別が保存された依頼は触っていないのに 400 になる。
  const save = (): void => {
    editMutation.mutate(
      {
        id: request.id,
        edit: {
          background: backgroundDraft,
          requested: requestedDraft,
          wfid: wfidDraft.trim() === '' ? null : wfidDraft.trim(),
          csTicketUrl: csUrlDraft.trim() === '' ? null : csUrlDraft.trim(),
          ...(categoryDraft === request.category ? {} : { category: categoryDraft }),
          ...(serviceDraft === request.service ? {} : { service: serviceDraft }),
          ...(userIdsDirty ? { userIds: draftUserIds } : {}),
        },
      },
      {
        onSuccess: () => {
          setEditing(false);
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader
        title={t('ops.detail.requestDetails')}
        action={
          editing ? null : (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-[var(--color-text-muted)]">
                {t('ops.detail.translate.label')}
              </span>
              {translateOptions.map((o) => (
                <Button
                  key={o.value}
                  type="button"
                  variant={translationLang === o.value ? 'primary' : 'ghost'}
                  size="sm"
                  disabled={isTranslating}
                  onClick={() => {
                    onTranslate(o.value);
                  }}
                >
                  {t(o.labelKey)}
                </Button>
              ))}
              {translationLang !== null && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={isTranslating}
                  onClick={onResetTranslation}
                >
                  {t('ops.detail.translate.toOriginal')}
                </Button>
              )}
              {editable && (
                <Button type="button" variant="secondary" size="sm" onClick={openEdit}>
                  {t('ops.detail.content.edit')}
                </Button>
              )}
            </div>
          )
        }
      />
      <CardContent className="space-y-2 text-sm text-[var(--color-text-secondary)]">
        {/* 編集中は保存済みバッジを出さない（ドロップダウンの選択値と保存まで食い違って見えるため）。
            種別を変えられない依頼は編集中もバッジのまま見せる。 */}
        {(!editing || categoryLocked) && (
          <p className="flex flex-wrap items-center gap-1.5">
            <span className="font-semibold text-[var(--color-text-primary)]">
              {t('ops.detail.content.category')}
            </span>
            <CategoryBadge category={request.category} />
          </p>
        )}
        {request.slackThreadUrl !== null && (
          <p>
            <a
              href={request.slackThreadUrl}
              target="_blank"
              rel="noreferrer"
              className="text-[var(--color-accent-primary)] hover:underline"
            >
              {t('ops.detail.openSlackThread')}
            </a>
          </p>
        )}
        {isTranslating && (
          <p className="text-xs text-[var(--color-text-muted)]">
            {t('ops.detail.translate.translating')}
          </p>
        )}
        {isTranslateError && (
          <p className="text-xs text-[var(--color-error)]">{t('ops.detail.translate.failed')}</p>
        )}

        {editing ? (
          <div className="space-y-3">
            <div className="space-y-1">
              <label
                htmlFor="ops-edit-background"
                className="block text-xs font-semibold text-[var(--color-text-primary)]"
              >
                {t('ops.detail.background')}
              </label>
              <Textarea
                id="ops-edit-background"
                value={backgroundDraft}
                onChange={(e) => {
                  setBackgroundDraft(e.target.value);
                }}
                rows={4}
                disabled={editMutation.isPending}
              />
            </div>
            <div className="space-y-1">
              <label
                htmlFor="ops-edit-requested"
                className="block text-xs font-semibold text-[var(--color-text-primary)]"
              >
                {t('ops.detail.requested')}
              </label>
              <Textarea
                id="ops-edit-requested"
                value={requestedDraft}
                onChange={(e) => {
                  setRequestedDraft(e.target.value);
                }}
                rows={3}
                disabled={editMutation.isPending}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {!categoryLocked && (
                <Select
                  label={t('ops.detail.content.category')}
                  value={categoryDraft}
                  options={categoryOptions.map((c) => ({
                    value: c,
                    label: opsCategoryLabel(c, t),
                  }))}
                  onChange={(e) => {
                    setCategoryDraft(e.target.value as OpsCategory);
                  }}
                  disabled={editMutation.isPending}
                  fullWidth
                />
              )}
              <Select
                label={t('ops.detail.content.service')}
                value={serviceDraft}
                options={SERVICE_OPTIONS.map((sv) => ({
                  value: sv,
                  label: OPS_SERVICE_LABELS[sv],
                }))}
                onChange={(e) => {
                  setServiceDraft(e.target.value as OpsService);
                }}
                disabled={editMutation.isPending}
                fullWidth
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label={t('ops.detail.content.wfid')}
                value={wfidDraft}
                onChange={(e) => {
                  setWfidDraft(e.target.value);
                }}
                placeholder={t('ops.detail.content.wfidPlaceholder')}
                disabled={editMutation.isPending}
                fullWidth
              />
              <Input
                label={t('ops.detail.content.csUrl')}
                value={csUrlDraft}
                onChange={(e) => {
                  setCsUrlDraft(e.target.value);
                }}
                placeholder={t('ops.detail.content.csUrlPlaceholder')}
                disabled={editMutation.isPending}
                fullWidth
              />
            </div>
            <Input
              label={t('ops.detail.targetUsers')}
              value={userIdsDraft}
              onChange={(e) => {
                setUserIdsDraft(e.target.value);
              }}
              placeholder={t('ops.detail.content.userIdsPlaceholder')}
              helperText={t('ops.detail.content.userIdsHelp')}
              error={userIdsInvalid ? t('ops.submit.errors.userIdsNotDigits') : undefined}
              disabled={editMutation.isPending}
              fullWidth
            />
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={editMutation.isPending || userIdsInvalid}
                onClick={save}
              >
                {t('ops.detail.content.save')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={editMutation.isPending}
                onClick={cancelEdit}
              >
                {t('ops.detail.content.cancel')}
              </Button>
              {editMutation.isError && (
                <span className="text-xs text-[var(--color-error)]">
                  {t('ops.detail.content.saveFailed')}
                </span>
              )}
            </div>
          </div>
        ) : (
          <>
            <p className="whitespace-pre-wrap">
              <span className="font-semibold text-[var(--color-text-primary)]">
                {t('ops.detail.background')}
              </span>
              {shownBackground}
            </p>
            {shownRequested !== '' && (
              <p className="whitespace-pre-wrap">
                <span className="font-semibold text-[var(--color-text-primary)]">
                  {t('ops.detail.requested')}
                </span>
                {shownRequested}
              </p>
            )}
            <p className="flex flex-wrap items-center gap-1.5">
              <span className="font-semibold text-[var(--color-text-primary)]">
                {t('ops.detail.targetUsers')}
              </span>
              {request.userIds.length > 0 ? (
                <span>{request.userIds.join(', ')}</span>
              ) : editable ? (
                <button
                  type="button"
                  onClick={openEdit}
                  className="font-semibold text-[var(--color-accent-primary)] hover:underline"
                >
                  {t('ops.detail.content.addUserIds')}
                </button>
              ) : (
                <span className="text-[var(--color-text-muted)]">
                  {t('ops.detail.content.none')}
                </span>
              )}
            </p>
            <p className="flex flex-wrap items-center gap-1.5">
              <span className="font-semibold text-[var(--color-text-primary)]">
                {t('ops.detail.content.wfid')}
              </span>
              {request.wfid !== null && request.wfid !== '' ? (
                <span>{request.wfid}</span>
              ) : editable ? (
                <button
                  type="button"
                  onClick={openEdit}
                  className="font-semibold text-[var(--color-accent-primary)] hover:underline"
                >
                  {t('ops.detail.content.addWfid')}
                </button>
              ) : (
                <span className="text-[var(--color-text-muted)]">
                  {t('ops.detail.content.none')}
                </span>
              )}
            </p>
            <p className="flex flex-wrap items-center gap-1.5">
              <span className="font-semibold text-[var(--color-text-primary)]">
                {t('ops.detail.content.csUrl')}
              </span>
              {request.csTicketUrl !== null && request.csTicketUrl !== '' ? (
                <a
                  href={request.csTicketUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[var(--color-accent-primary)] hover:underline"
                >
                  {t('ops.detail.content.openCsUrl')}
                </a>
              ) : (
                <span className="text-[var(--color-text-muted)]">
                  {t('ops.detail.content.none')}
                </span>
              )}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
